import { Archive, Bell, Lightbulb, Pencil, Tag, Trash2, type LucideIcon } from 'lucide-react';
import { NavLink } from 'react-router';
import { useLabels } from '../api/labels';
import { useIsMobile } from '../hooks/use-media-query';
import { cn } from '../lib/cn';
import { paths } from '../lib/routes';
import { useUi } from '../stores/ui';

function NavItem({
  to,
  icon: Icon,
  label,
  expanded,
  onClick,
  end,
}: {
  to?: string;
  icon: LucideIcon;
  label: string;
  expanded: boolean;
  onClick?: () => void;
  end?: boolean;
}) {
  const className = (active: boolean) =>
    cn(
      'group flex h-12 w-full shrink-0 items-center gap-8 rounded-r-full pl-3 text-left text-sm font-medium transition-[background-color,width] duration-150',
      // Réduit, l'élément actif devient une pastille ronde autour de l'icône (comme Keep).
      expanded ? 'pl-6' : 'ml-3 w-12 rounded-full pl-3',
      active ? 'bg-nav-active text-fg' : 'text-fg hover:bg-hover',
    );
  const content = (
    <>
      <Icon
        size={22}
        className="shrink-0 text-muted group-aria-[current=page]:text-fg"
        aria-hidden
      />
      <span className={cn('truncate', !expanded && 'sr-only')}>{label}</span>
    </>
  );
  if (!to) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={className(false)}
        title={expanded ? undefined : label}
      >
        {content}
      </button>
    );
  }
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      className={({ isActive }) => className(isActive)}
      title={expanded ? undefined : label}
    >
      {content}
    </NavLink>
  );
}

export function Sidebar() {
  const { data: labels = [] } = useLabels();
  const isMobile = useIsMobile();
  const sidebarExpanded = useUi((s) => s.sidebarExpanded);
  const mobileOpen = useUi((s) => s.mobileSidebarOpen);
  const closeMobile = useUi((s) => s.closeMobileSidebar);
  const openLabels = useUi((s) => s.setLabelsDialogOpen);

  const expanded = isMobile || sidebarExpanded;
  const onNavigate = isMobile ? closeMobile : undefined;

  const nav = (
    <nav aria-label="Navigation principale" className="flex flex-col py-2">
      <NavItem
        to={paths.notes}
        end
        icon={Lightbulb}
        label="Notes"
        expanded={expanded}
        onClick={onNavigate}
      />
      <NavItem
        to={paths.reminders}
        icon={Bell}
        label="Rappels"
        expanded={expanded}
        onClick={onNavigate}
      />
      {labels.map((label) => (
        <NavItem
          key={label.id}
          to={paths.label(label.id)}
          icon={Tag}
          label={label.name}
          expanded={expanded}
          onClick={onNavigate}
        />
      ))}
      <NavItem
        icon={Pencil}
        label="Modifier les libellés"
        expanded={expanded}
        onClick={() => {
          onNavigate?.();
          openLabels(true);
        }}
      />
      <NavItem
        to={paths.archive}
        icon={Archive}
        label="Archives"
        expanded={expanded}
        onClick={onNavigate}
      />
      <NavItem
        to={paths.trash}
        icon={Trash2}
        label="Corbeille"
        expanded={expanded}
        onClick={onNavigate}
      />
    </nav>
  );

  if (isMobile) {
    return (
      <>
        {mobileOpen && (
          <div
            className="animate-fade-in fixed inset-0 top-16 z-30 bg-black/40"
            onClick={closeMobile}
            aria-hidden
          />
        )}
        <aside
          className={cn(
            'scrollbar-thin fixed top-16 bottom-0 left-0 z-40 w-72 overflow-y-auto bg-bg shadow-dialog transition-transform duration-200',
            mobileOpen ? 'translate-x-0' : '-translate-x-full',
          )}
        >
          {nav}
        </aside>
      </>
    );
  }

  return (
    <aside
      className={cn(
        'scrollbar-thin sticky top-16 h-[calc(100dvh-4rem)] shrink-0 overflow-x-hidden overflow-y-auto transition-[width] duration-200',
        sidebarExpanded ? 'w-72' : 'w-20',
      )}
    >
      {nav}
    </aside>
  );
}
